import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { listCategoriesTree } from "../services/firestore";
import { useApp } from "../contexts/AppContext";

/**
 * CategoryMegaMenu — minimalist 3-level dropdown (no images).
 *  - Left column: top-level categories
 *  - Right column: subcategories (level 2) — each shows its level-3 children as small links
 */
export default function CategoryMegaMenu({ open, onClose, label }) {
  const { t } = useApp();
  const [tree, setTree] = useState([]);
  const [hovered, setHovered] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    listCategoriesTree().then(setTree).catch(() => setTree([]));
  }, [open]);

  useEffect(() => { if (tree.length && !hovered) setHovered(tree[0]); }, [tree, hovered]);
  useEffect(() => { if (!open) setHovered(null); }, [open]);

  const go = (path) => { onClose?.(); nav(path); };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-x-0 bottom-0 top-[110px] z-40 bg-black/30 backdrop-blur-[2px]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={onClose}
            data-testid="mega-overlay"
          />
          <motion.div
            className="absolute left-0 right-0 top-full z-50 bg-[var(--bg)] border-t border-[var(--border-soft)] border-b border-[var(--border)] overflow-hidden"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onMouseLeave={onClose}
            data-testid="mega-menu"
          >
            <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-7 grid md:grid-cols-12 gap-10">
              {/* LEFT — top-level categories */}
              <div className="md:col-span-4 lg:col-span-3">
                <div className="metadata text-[var(--text-muted)] mb-5">{label || t.nav.catalog}</div>
                <ul className="mega-stagger space-y-1">
                  {tree.map((c) => {
                    const isActive = hovered?.id === c.id;
                    return (
                      <li key={c.id}>
                        <button
                          onMouseEnter={() => setHovered(c)}
                          onFocus={() => setHovered(c)}
                          onClick={() => go(`/catalog?category=${encodeURIComponent(c.slug)}`)}
                          className={`group w-full text-left flex items-center justify-between section-title text-2xl md:text-3xl py-1.5 transition-colors ${isActive ? "text-[var(--primary)]" : "text-[var(--text-2)] hover:text-[var(--text)]"}`}
                          data-testid={`mega-cat-${c.slug}`}
                        >
                          <span className="relative inline-flex items-center gap-3">
                            <span className={`inline-block h-px transition-all ${isActive ? "w-7 bg-[var(--primary)]" : "w-0 bg-[var(--text-muted)]"}`} />
                            <span>{c.name}</span>
                          </span>
                          <ArrowRight size={16} className={`transition-all ${isActive ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"}`} />
                        </button>
                      </li>
                    );
                  })}
                  {tree.length === 0 && (
                    <li className="text-[var(--text-muted)] metadata py-2">{t.mega.noSub}</li>
                  )}
                </ul>
                <Link
                  onClick={onClose}
                  to="/catalog"
                  className="metadata draw-underline mt-8 inline-flex items-center gap-2 hover:text-[var(--primary)]"
                  data-testid="mega-see-all"
                >
                  {t.mega.seeFullCatalog}
                </Link>
              </div>

              {/* RIGHT — sub-categories (level 2) with their children (level 3) */}
              <div className="md:col-span-8 lg:col-span-9 border-l border-[var(--border-soft)] pl-10">
                <AnimatePresence mode="wait">
                  {hovered && (
                    <motion.div
                      key={hovered.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div className="flex items-end justify-between mb-4">
                        <div>
                          <div className="metadata text-[var(--text-muted)]">{t.mega.category}</div>
                          <div className="section-title text-2xl md:text-3xl mt-1">{hovered.name}</div>
                          {hovered.description && (
                            <p className="text-[var(--text-2)] text-sm max-w-xl leading-relaxed mt-2">{hovered.description}</p>
                          )}
                        </div>
                        <button
                          onClick={() => go(`/catalog?category=${encodeURIComponent(hovered.slug)}`)}
                          className="metadata draw-underline hover:text-[var(--primary)] flex items-center gap-2"
                        >
                          {t.mega.viewAll} <ArrowRight size={12} />
                        </button>
                      </div>

                      {/* SUB-CATEGORIES GRID — each subcategory shows its sub-sub-categories (and deeper) underneath */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-6 mega-stagger">
                        {(hovered.children || []).map((sub) => (
                          <div key={sub.id} className="min-w-0">
                            <button
                              onClick={() => go(`/catalog?category=${encodeURIComponent(sub.slug)}`)}
                              className="text-left w-full group"
                              data-testid={`mega-sub-${sub.slug}`}
                            >
                              <div className="font-display text-[17px] md:text-[18px] font-semibold text-[var(--text)] group-hover:text-[var(--primary)] transition-colors leading-snug">
                                {sub.name}
                              </div>
                            </button>

                            {/* Level 3+ children (fully recursive) */}
                            {sub.children && sub.children.length > 0 && (
                              <ul className="mt-2.5 space-y-1 border-l border-[var(--border-soft)] pl-3">
                                {sub.children.map((c3) => renderMegaChild(c3, go, 0))}
                              </ul>
                            )}
                          </div>
                        ))}
                        {!hovered.children?.length && (
                          <div className="col-span-full text-[var(--text-muted)] text-sm metadata py-4">{t.mega.noSub}</div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * Recursively render sub-category children (level 3, 4, 5, ...).
 * Each nested level is indented with a slightly smaller font.
 */
function renderMegaChild(node, go, depth) {
  const hasChildren = node.children && node.children.length > 0;
  // Larger, more legible font sizes across all levels (14.5 → 13.5 → 12.5px)
  const fontSize = depth === 0 ? 14.5 : depth === 1 ? 13.5 : 12.5;
  return (
    <li key={node.id}>
      <button
        onClick={() => go(`/catalog?category=${encodeURIComponent(node.slug)}`)}
        className="text-[var(--text-2)] hover:text-[var(--primary)] transition-colors text-left leading-snug block py-0.5"
        style={{ fontSize: `${fontSize}px` }}
        data-testid={`mega-sub-${depth + 3}-${node.slug}`}
      >
        {node.name}
      </button>
      {hasChildren && (
        <ul className="mt-1 space-y-1 border-l border-[var(--border-soft)] pl-3 ml-1">
          {node.children.map((child) => renderMegaChild(child, go, depth + 1))}
        </ul>
      )}
    </li>
  );
}
